import fp from 'lodash/fp';
import { push } from 'react-router-redux';
import { all, put, select, takeLatest } from 'redux-saga/effects';
import { routes } from '../../../../constants';
import { TaskStatus } from '../../../../constants/tasks';
import { apiActionResult } from '../../common';
import { authTokenSelector } from '../../session';
import userActions from '../../user/actions';
import { actions as businessActions } from '../business/actions';
import { actions, apiActions } from './actions';

export function* handleBusinessResource(action) {
  const authToken = yield select(authTokenSelector);
  if (authToken.idToken) {
    switch (action.type) {
      case actions.business.team.setTeamTrainingPlan().type: {
        const {
          cpdCategorys,
          businessId,
          businessTeamId,
          userId,
          industryId,
          industry,
          goals
        } = action.payload;

        yield put(businessActions.business.updateTaskStack(businessTeamId, TaskStatus.updating))

        yield put(apiActions.business.team.updateIndustryRole(authToken, businessId, businessTeamId, industry));
        yield put(apiActions.business.team.setTeamProgressGoals(authToken, businessId, businessTeamId, goals));

        const updateIndustryRoleResult = yield apiActionResult(actions.business.team.api.updateIndustryRole);
        const updateGoalsResult = yield apiActionResult(actions.business.team.api.setTeamProgressGoals);

        if (updateIndustryRoleResult.success && updateGoalsResult.success) {
          yield put(apiActions.business.team.setTeamTrainingPlan(authToken, businessId, businessTeamId, {
            industryId: industryId,
            pendingCpdCategorys: cpdCategorys
          }));
        }

        const setCpdCategorysResult = yield apiActionResult(actions.business.team.api.setTeamTrainingPlan);

        if (setCpdCategorysResult.success) {
          yield put(apiActions.business.team.getTeamTrainingPlan(authToken, businessId, businessTeamId));
          yield put(userActions.user.getCpdCategorys(userId))
          yield put(businessActions.business.updateTaskStack(businessTeamId, TaskStatus.completed, {
            ...updateIndustryRoleResult.success.payload,
            ...setCpdCategorysResult.success.payload
          }))
        } else {
          yield put(businessActions.business.updateTaskStack(businessTeamId, TaskStatus.completed))
        }

        break;
      }
      case actions.business.team.getTeams().type:
        yield put(apiActions.business.team.getTeams(authToken, action.payload.businessId, action.payload.query));
        break;
      case actions.business.team.loadMoreTeams().type:
        yield put(apiActions.business.team.loadMoreTeams(authToken, action.payload.businessId, action.payload.query));
        break;
      case actions.business.team.loadMoreTeamsWithStats().type:
        yield put(apiActions.business.team.loadMoreTeamsWithStats(authToken, action.payload.businessId, action.payload.query));
        break;
      case actions.business.team.getTeamsWithStats().type:
        yield put(apiActions.business.team.getTeamsWithStats(authToken, action.payload.businessId, action.payload.query));
        break;
      case actions.business.team.getTeamsWithProgress().type:
        yield put(apiActions.business.team.getTeamsWithProgress(authToken, action.payload.businessId, action.payload.query));
        break;
      case actions.business.team.loadMoreTeamsWithProgress().type:
        yield put(apiActions.business.team.loadMoreTeamsWithProgress(authToken, action.payload.businessId, action.payload.query));
        break;
      case actions.business.team.getTeamIndustryRoles().type:
        yield put(apiActions.business.team.getTeamIndustryRoles(authToken, action.payload.businessId, action.payload.businessTeamId));
        break;
      case actions.business.team.getTeamTrainingPlan().type:
        yield put(apiActions.business.team.getTeamTrainingPlan(authToken, action.payload.businessId, action.payload.businessTeamId));
        break;
      case actions.business.team.getTeamCpdCategorys().type:
        yield put(apiActions.business.team.getTeamCpdCategorys(authToken, action.payload.businessId, action.payload.businessTeamId));
        break;
      case actions.business.team.getTeamPendingExternalCpd().type:
        yield put(apiActions.business.team.getTeamPendingExternalCpd(authToken, action.payload.businessId, action.payload.businessTeamId));
        break;
      case actions.business.team.getTeam().type: {
        const {
          businessId,
          businessTeamId
        } = action.payload;
        yield put(apiActions.business.team.getTeam(authToken, businessId, businessTeamId));
        break;
      }
      case actions.business.team.getTeamStats().type: {
        const {
          businessId,
          businessTeamId
        } = action.payload;
        yield put(apiActions.business.team.getTeamStats(authToken, businessId, businessTeamId));
        break;
      }
      case actions.business.team.getTeamMembers().type: {
        const {
          businessId,
          businessTeamId,
          query
        } = action.payload;
        yield put(apiActions.business.team.getTeamMembers(authToken, businessId, businessTeamId, query));
        break;
      }
      case actions.business.team.loadMoreMembers().type: {
        const {
          businessId,
          businessTeamId,
          query
        } = action.payload;
        yield put(apiActions.business.team.loadMoreMembers(authToken, businessId, businessTeamId, query));
        break;
      }
      case actions.business.team.createTeam().type: {
        const {
          businessId,
          teamName,
          users,
          managers,
          nonMemberManagers,
          noRedirect
        } = action.payload;

        yield put(apiActions.business.team.createTeam(authToken, businessId,
          { name: teamName, members: users, managers, nonMemberManagers }));

        const createResult = yield apiActionResult(actions.business.team.api.createTeam);

        if (createResult.success) {
          if (noRedirect) {
            yield put(apiActions.business.team.getTeams(authToken, businessId, {}));
            yield put(businessActions.business.getTeamMembers(businessId));
          } else {
            const newTeamId = fp.get('payload.team.businessTeam.id')(createResult.success)
            yield put(push({ pathname: routes.app.teams.teamMembers(newTeamId) }));
          }
        }
        break;
      }
      case actions.business.team.removeTeam().type: {
        const {
          businessId,
          businessTeamId,
          noRedirect
        } = action.payload;

        yield put(apiActions.business.team.removeTeam(authToken, businessId, businessTeamId));

        const removeResult = yield apiActionResult(actions.business.team.api.removeTeam);

        if (removeResult.success) {
          if (noRedirect) {
            yield put(apiActions.business.team.getTeams(authToken, businessId, {}));
            yield put(businessActions.business.getTeamMembers(businessId));
          } else {
            yield put(push({ pathname: routes.app.teams.root }));
          }
        }
        break;
      }
      case actions.business.team.addTeamManager().type: {
        const {
          businessId,
          businessTeamId,
          user
        } = action.payload;

        yield put(apiActions.business.team.addTeamManager(authToken, businessId, businessTeamId, user));

        const managerResult = yield apiActionResult(actions.business.team.api.addTeamManager);

        if (managerResult.success) {
          yield put(businessActions.business.getTeamMembers(businessId));
          yield put(apiActions.business.team.getTeam(authToken, businessId, businessTeamId));
        }
        break;
      }
      case actions.business.team.removeTeamManager().type: {
        const {
          businessId,
          businessTeamId,
          user
        } = action.payload;

        yield put(apiActions.business.team.removeTeamManger(authToken, businessId, businessTeamId, user));

        const managerResult = yield apiActionResult(actions.business.team.api.removeTeamManager);

        if (managerResult.success) {
          yield put(businessActions.business.getTeamMembers(businessId));
          yield put(apiActions.business.team.getTeam(authToken, businessId, businessTeamId));
        }
        break;
      }
      case actions.business.team.removeTeamMember().type: {
        const {
          businessId,
          businessTeamId,
          user
        } = action.payload;

        yield put(apiActions.business.team.removeTeamMember(authToken, businessId, businessTeamId, user));

        const memberResult = yield apiActionResult(actions.business.team.api.removeTeamMember);

        if (memberResult.success) {
          yield put(apiActions.business.team.getTeam(authToken, businessId, businessTeamId));
          yield put(apiActions.business.unassignedTeamMembers(authToken, businessId));
        }
        break;
      }
      case actions.business.team.addTeamMembers().type: {
        const {
          businessId,
          businessTeamId,
          users
        } = action.payload;

        yield put(apiActions.business.team.addTeamMembers(authToken, businessId, businessTeamId, users));

        const memberResult = yield apiActionResult(actions.business.team.api.addTeamMembers);

        if (memberResult.success) {
          yield put(push({ pathname: routes.app.teams.teamMembers(businessTeamId) }));
        }
        break;
      }
      case actions.business.team.setTeamMembers().type: {
        const {
          businessId,
          businessTeamId,
          users,
          managers
        } = action.payload;

        yield put(apiActions.business.team.setTeamMembers(authToken, businessId, businessTeamId, users, managers));

        const memberResult = yield apiActionResult(actions.business.team.api.setTeamMembers);

        if (memberResult.success) {
          yield put(apiActions.business.team.getTeams(authToken, businessId, {}));
          yield put(businessActions.business.getTeamMembers(businessId));
        }
        break;
      }
      default:
        console.error(`Unknown authenticated action received [${action.type}].`);
    }
  }
  else {
    switch (action.type) {
      default:
        console.error(`Unknown common action received [${action.type}].`);
    }
  }
}

export function* renameTeam(action) {
  const authToken = yield select(authTokenSelector);
  const {
    businessId,
    businessTeamId,
    newTeam
  } = action.payload;
  yield put(apiActions.business.team.renameTeam(authToken, businessId, businessTeamId, newTeam));
}

export function* updateTeam(action) {
  const {
    businessId,
    businessTeamId,
    newTeam,
    users,
    managers,
    nonMemberManagers
  } = action.payload;

  yield put(actions.business.team.renameTeam(businessId, businessTeamId, newTeam));
  yield put(actions.business.team.setNonMemberManagers(businessId, businessTeamId, nonMemberManagers));
  yield put(actions.business.team.setTeamMembers(businessId, businessTeamId, users));

  const teamResult = yield apiActionResult(actions.business.team.api.renameTeam);
  const nonMemberManagerResult = yield apiActionResult(actions.business.team.api.setNonMemberManagers);
  const memberResult = yield apiActionResult(actions.business.team.api.setTeamMembers);

  if (teamResult.success && nonMemberManagerResult.success && memberResult.success) {
    yield put(actions.business.team.setManagers(businessId, businessTeamId, managers));

    const managerResult = yield apiActionResult(actions.business.team.api.setManagers);
    if (managerResult.success) {
      yield put(push({ pathname: routes.app.teams.teamMembers(businessTeamId) }));
    }
  }
}

export function* setManagers(action) {
  const authToken = yield select(authTokenSelector);
  const {
    businessId,
    businessTeamId,
    managers
  } = action.payload;
  yield put(apiActions.business.team.setManagers(authToken, businessId, businessTeamId, managers));

  const managerResult = yield apiActionResult(actions.business.team.api.setManagers);
  if (managerResult.success) {
    yield put(apiActions.business.team.getTeams(authToken, businessId, {}));
    yield put(businessActions.business.getTeamMembers(businessId));
  }
}

export function* setNonMemberManagers(action) {
  const authToken = yield select(authTokenSelector);
  const {
    businessId,
    businessTeamId,
    nonMemberManagers
  } = action.payload;
  yield put(apiActions.business.team.setNonMemberManagers(authToken, businessId, businessTeamId, nonMemberManagers));

  const nonMemberManagerResult = yield apiActionResult(actions.business.team.api.setNonMemberManagers);
  if (nonMemberManagerResult.success) {
    yield put(apiActions.business.team.getTeams(authToken, businessId, {}));
    yield put(businessActions.business.getTeamMembers(businessId));
  }
}

export function* getNonMemberManagers(action) {
  const authToken = yield select(authTokenSelector);
  const {
    businessId,
    businessTeamId
  } = action.payload;
  yield put(apiActions.business.team.getNonMemberManagers(authToken, businessId, businessTeamId));
}

export const sagas = all([
  takeLatest([
    actions.business.team.getTeams().type,
    actions.business.team.loadMoreTeams().type,
    actions.business.team.loadMoreTeamsWithStats().type,
    actions.business.team.getTeamsWithStats().type,
    actions.business.team.getTeamsWithProgress().type,
    actions.business.team.loadMoreTeamsWithProgress().type,
    actions.business.team.getTeam().type,
    actions.business.team.getTeamStats().type,
    actions.business.team.getTeamMembers().type,
    actions.business.team.loadMoreMembers().type,
    actions.business.team.getTeamIndustryRoles().type,
    actions.business.team.getTeamCpdCategorys().type,
    actions.business.team.getTeamTrainingPlan().type,
    actions.business.team.setTeamTrainingPlan().type,
    actions.business.team.getTeamPendingExternalCpd().type,
    actions.business.team.createTeam().type,
    actions.business.team.removeTeam().type,
    actions.business.team.addTeamManager().type,
    actions.business.team.removeTeamManager().type,
    actions.business.team.removeTeamMember().type,
    actions.business.team.addTeamMembers().type,
    actions.business.team.setTeamMembers().type
  ], handleBusinessResource),
  takeLatest(actions.business.team.renameTeam().type, renameTeam),
  takeLatest(actions.business.team.update().type, updateTeam),
  takeLatest(actions.business.team.setManagers().type, setManagers),
  takeLatest(actions.business.team.setNonMemberManagers().type, setNonMemberManagers),
  takeLatest(actions.business.team.getNonMemberManagers().type, getNonMemberManagers)
])

function* teamSagas() {
  yield sagas;
}

export default teamSagas;


// WEBPACK FOOTER //
// ./src/state/modules/business/team/sagas.js